/**
 * Ana ekrana ekleme (PWA kurulumu). Android Chrome "beforeinstallprompt"
 * olayını sayfa açılır açılmaz bir kez atıyor; erken yakalanıp saklanıyor ki
 * mutfak ekranındaki "Ana ekrana ekle" düğmesi sonradan kullanabilsin. iPad ve
 * iPhone bu olayı hiç atmıyor; orada yönerge gösteriliyor.
 */

interface InstallPrompt extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: InstallPrompt | null = null
const listeners = new Set<() => void>()

export function captureInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferred = e as InstallPrompt
    listeners.forEach((fn) => fn())
  })
  window.addEventListener('appinstalled', () => {
    deferred = null
    listeners.forEach((fn) => fn())
  })
}

export const canPromptInstall = () => deferred !== null

export function onInstallChange(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/** Android kurulum penceresini açar. Kuruldu mu döner. */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false
  const d = deferred
  deferred = null
  await d.prompt()
  const r = await d.userChoice.catch(() => ({ outcome: 'dismissed' as const }))
  listeners.forEach((fn) => fn())
  return r.outcome === 'accepted'
}

export function isIOS(): boolean {
  const ua = navigator.userAgent
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

/** iPad (yeni iPad'ler kendini Mac gibi tanıtıyor, dokunmatik olmasından anlaşılıyor). */
export function isIPad(): boolean {
  const ua = navigator.userAgent
  return /iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

/**
 * Android tablet: telefonlar tarayıcı kimliğinde "Mobile" taşıyor, tabletler
 * taşımıyor. Büyük tabletlerde Chrome varsayılan olarak masaüstü sitesini
 * istiyor ve kendini Linux bilgisayar gibi tanıtıyor ("Android" yok); onu
 * dokunmatik ekranından tanıyoruz (1 Kas 2026, kiosk tableti bağlanamadı).
 */
export function isAndroidTablet(): boolean {
  const ua = navigator.userAgent
  if (/Android/.test(ua)) return !/Mobile/.test(ua)
  return /Linux/.test(ua) && navigator.maxTouchPoints > 0 && matchMedia('(pointer: coarse)').matches
}

/** Tablet (iPad ya da Android tablet). */
export const isTablet = () => isIPad() || isAndroidTablet()

/** Ana ekrandan (uygulama olarak) mı açıldı. */
export function isStandalone(): boolean {
  try {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true
    )
  } catch {
    return false
  }
}
